<script setup>
import { usePage, useForm } from '@inertiajs/vue3';
import { ref, reactive, watch, computed, onMounted, onBeforeUnmount, nextTick } from 'vue';
import ziggyRoute from '../ziggyRoute';
import eventBus from '../eventBus';
import Echo from 'laravel-echo';
import io from 'socket.io-client';
import SpeechRecognizer from '../../speech-recognize';

window.io = io;

/**
 * To prevent "Extraneous non-props attributes (errors, ...)" warning.
 * @see https://github.com/inertiajs/inertia/issues/543
 */
defineOptions({ inheritAttrs: false });

// 後端來的資料
const props = defineProps({
    ziggyConfig: Object,
    echoHost: String,
    languageOptions: Object,
    languageNativeOptions: Object,
    defaultSourceLanguageValue: Number,
    defaultTargetLanguageValue: Number,
    recognizeServices: {
        type: Object,
        validator: services => [
            'azure',
            'mimi',
        ].every(type => services.hasOwnProperty(type)),
    },
    recognizeServiceLanguages: Object,
    japaneseLanguageValue: Number,
});

// 登出表單
const page = usePage();
const form = useForm({});
const submitLogoutForm = () => {
    stopRecognize(false);
    form.transform(data => ({
        _token: page.props.csrfToken,
        ...data,
    })).post(ziggyRoute(props, 'logout'));
}

// 後台停用使用者時，前台立即強制登出
(props.echoHost && page.props.authUser?.id) && new Echo({
    broadcaster: 'socket.io',
    host: props.echoHost,
    transports: ['websocket']
}).private(`member.${page.props.authUser?.id}`).listen(
    '.disable.member',
    submitLogoutForm
);

// 按下 logo 事件
// const onClickLogo = event => eventBus.onClickLogo = event;
const onClickLogo = (event) => {
    languageSelects.source.openedOptionsFromUI = false;
    languageSelects.target.openedOptionsFromUI = false;

    eventBus.onClickLogo = event
}

// 清空翻譯視窗
const phrases = ref([]);
const resetTranslationWindow = () => phrases.value = [];

// 已選擇的來源與目標語言
const languageSelects = reactive({
    source: {
        selected: +props.defaultSourceLanguageValue,
        selectedText: props.languageOptions[props.defaultSourceLanguageValue] || '',
        selectedNativeText: props.languageNativeOptions[props.defaultSourceLanguageValue] || '',
        openedOptionsFromModal: false,
        openedOptionsFromUI: false,
    },
    target: {
        selected: +props.defaultTargetLanguageValue,
        selectedText: props.languageOptions[props.defaultTargetLanguageValue] || '',
        selectedNativeText: props.languageNativeOptions[props.defaultTargetLanguageValue] || '',
        openedOptionsFromModal: false,
        openedOptionsFromUI: false,
    },
});

// 開合語言選單
const toggleLanguageOptions = (type, from) => {
    if (from === 'ui') {
        languageSelects[type].openedOptionsFromUI = !languageSelects[type].openedOptionsFromUI;
        languageSelects[(type === 'source') ? 'target' : 'source'].openedOptionsFromUI = false;
    }

    if (from === 'modal') {
        languageSelects[type].openedOptionsFromModal = !languageSelects[type].openedOptionsFromModal;
        languageSelects[(type === 'source') ? 'target' : 'source'].openedOptionsFromModal = false;
    }
};

// 選擇選單中的語言
const selectLanguage = (type, selected, from) => {
    if (!recognizeButtonValues.traAuto) {
        const sourceLanguage = type === 'source' ? +selected : languageSelects.source.selected;
        const targetLanguage = type === 'target' ? +selected : languageSelects.target.selected;
        const asrNotSupport = !isRecognizeServiceLanguageSupport(recognizeButtonValues.asr, sourceLanguage),
            traNotSupport = !isTraSupport(recognizeButtonValues.tra, sourceLanguage, targetLanguage);
        if (asrNotSupport || traNotSupport) {
            openAsrOrTraNotSupportModal(
                recognizeButtonValues.asr,
                recognizeButtonValues.tra,
                asrNotSupport,
                traNotSupport
            );
            return;
        }
    }
    languageSelects[type].selected = +selected;
    languageSelects[type].selectedText = props.languageOptions[selected] || '';
    languageSelects[type].selectedNativeText = props.languageNativeOptions[selected] || '';

    if (from === 'ui') {
        languageSelects[type].openedOptionsFromUI = false;
    }

    if (from === 'modal') {
        languageSelects[type].openedOptionsFromModal = false;
    }

    if (!recognizing.value) return;
    restartTimingAndRecognize();
};

// 交換來源與目標語言
const shouldExchangeLang = ref(false);
const swapSourceTargetLanguages = () => [
    languageSelects.source,
    languageSelects.target
] = [languageSelects.target, languageSelects.source];
const exchangeSourceTargetLanguages = () => {
    if (!shouldExchangeLang.value) {
        swapSourceTargetLanguages();
        if (!recognizing.value) return;
        if (!asrRecognized.value) {
            shouldExchangeLang.value = true;
            return;
        }
    }
    restartTimingAndRecognize();
}

// 顯藏主畫面操作列
const showUIToolbar = ref(true);
const toggleUIToolbar = () => showUIToolbar.value = !showUIToolbar.value;

// 主畫面操作列的按鈕設定值
const recognizeButtonValues = reactive({
    asr: 1,
    tra: 1,
    traAuto: false,
    lidAuto: false,
});

// 通用的提示彈窗
const initialNoticeModalSettings = {
    show: false,
    header: '',
    bodyMessage: '',
    bodyMessageBold: true,
    closeButtonText: 'Close',
    confirmCallback: null,
    confirmCallbackArgs: {},
};
const noticeModalSettings = ref(initialNoticeModalSettings);
const resetNoticeModalSettings = () => noticeModalSettings.value = JSON.parse(JSON.stringify(initialNoticeModalSettings));
const closeNoticeModal = () => noticeModalSettings.value.show = false;
const confirmNoticeModal = () => {
    noticeModalSettings.value.confirmCallback(noticeModalSettings.value.confirmCallbackArgs);
    closeNoticeModal();
};

// 語言不支援切換的提示彈窗
const openNotSupportModal = message => {
    resetNoticeModalSettings();
    noticeModalSettings.value.bodyMessage = message;
    noticeModalSettings.value.show = true;
}

// 語言可支援切換的提示彈窗
const openSupportModal = (modeTitle, setValueCallback, languageValue) => {
    resetNoticeModalSettings();
    noticeModalSettings.value.bodyMessage = `Convert ${modeTitle}?`;
    noticeModalSettings.value.confirmCallback = setValueCallback;
    noticeModalSettings.value.confirmCallbackArgs = { languageValue };
    noticeModalSettings.value.closeButtonText = 'Cancel';
    noticeModalSettings.value.show = true;
};

// 在特定的ASR或TRA模式下，語言是否可支援
const isRecognizeServiceLanguageSupport = (
    modeValue,
    languageValue
) => props.recognizeServiceLanguages[modeValue][languageValue] !== null;

// 切換ASR
const prepareToSetAsr = value => {
    if (value === recognizeButtonValues.asr) return;
    if (!isRecognizeServiceLanguageSupport(value, languageSelects.source.selected)) {
        openNotSupportModal(`ASR${value} does not support this language`);
        return;
    }
    openSupportModal('ASR', setAsr, value);
};
const setAsr = ({ languageValue }) => {
    recognizeButtonValues.asr = languageValue;
    if (!recognizing.value) return;
    restartTimingAndRecognize();
}

// 切換TRA
const isTraSupport = (traValue, sourceLanguage, targetLanguage) => {
    if (traValue === props.recognizeServices.azure) return true;
    if (
        sourceLanguage === props.japaneseLanguageValue &&
        isRecognizeServiceLanguageSupport(traValue, targetLanguage)
    ) return true;
    if (
        targetLanguage === props.japaneseLanguageValue &&
        isRecognizeServiceLanguageSupport(traValue, sourceLanguage)
    ) return true;
    return false;
}
const prepareToSetTra = value => {
    if (value === recognizeButtonValues.tra) return;
    if (value === props.recognizeServices.mimi && !isTraSupport(
        value,
        languageSelects.source.selected,
        languageSelects.target.selected
    )) {
        openNotSupportModal(`TRA${value} does not support this speech language and subtitle language`);
        return;
    }
    openSupportModal('TRA', setTra, value);
};
const setTra = ({ languageValue }) => {
    recognizeButtonValues.tra = languageValue;
    if (!recognizing.value) return;
    restartTimingAndRecognize();
}

// 開關TRA Auto
const toggleTraAuto = () => recognizeButtonValues.traAuto = !recognizeButtonValues.traAuto;
const getTraValueByAuto = (originalTraValue, sourceLanguage, targetLanguage) => {
    if (!recognizeButtonValues.traAuto) return originalTraValue;
    // 如果mimi TRA可以支援的話就優先用mimi TRA
    if (isTraSupport(
        props.recognizeServices.mimi,
        sourceLanguage,
        targetLanguage
    )) return props.recognizeServices.mimi;
    return props.recognizeServices.azure;
};

// 開關LID Auto
const toggleLidAuto = () => {
    recognizeButtonValues.lidAuto = !recognizeButtonValues.lidAuto;
    recognizer.when(
        recognizeButtonValues.lidAuto,
        recognizer => recognizer.openLid(),
        recognizer => recognizer.closeLid()
    );
}

// 操作彈窗各按鈕的狀態
const operateModalButtonStatuses = reactive({
    timer: false,
    align: false,
    reverseSpeech: false,
    reverseSubtitle: false,
    speech: true,
    subtitle: true,
    asr: false,
    tra: false,
    lid: false,
    fullscreen: false,
    logo: true,
    color: false,
    length: false,
});

// 操作彈窗各按鈕的狀態文字顯示
const TOGGLE_STATUS_TEXTS = {
    true: 'OFF',
    false: 'ON',
};
const ALIGN_STATUS_TEXTS = {
    true: 'LEFT',
    false: 'RIGHT',
}
const COLOR_STATUS_TEXTS = {
    true: 'BLACK',
    false: 'WHITE',
};
const LENGTH_STATUS_TEXTS = {
    true: 'FULL',
    false: 'HALF',
};
const operateModalButtonTexts = computed(() => Object.entries(operateModalButtonStatuses)
    .reduce((buttonTexts, [buttonName, buttonValue]) => {
        [
            'timer',
            'reverseSpeech',
            'reverseSubtitle',
            'speech',
            'subtitle',
            'asr',
            'tra',
            'lid',
            'fullscreen',
            'logo',
        ].includes(buttonName) && (buttonTexts[buttonName] = TOGGLE_STATUS_TEXTS[buttonValue]);
        (buttonName === 'align') && (buttonTexts[buttonName] = ALIGN_STATUS_TEXTS[buttonValue]);
        (buttonName === 'color') && (buttonTexts[buttonName] = COLOR_STATUS_TEXTS[buttonValue]);
        (buttonName === 'length') && (buttonTexts[buttonName] = LENGTH_STATUS_TEXTS[buttonValue]);
        return buttonTexts;
    }, {}));

// 變更操作彈窗特定按鈕的狀態
const toggleStatus = buttonType => operateModalButtonStatuses[buttonType] = !operateModalButtonStatuses[buttonType];

// 切換全螢幕
const inFullscreen = () => !!(
    document.fullscreenElement ||
    document.webkitFullscreenElement ||
    document.mozFullScreenElement ||
    document.msFullscreenElement
);
const toggleFullscreen = () => {
    const documentElement = document.documentElement;
    if (!inFullscreen()) {
        // 開啟全螢幕
        if (documentElement.requestFullscreen) {
            documentElement.requestFullscreen();
        } else if (documentElement.msRequestFullscreen) {
            documentElement.msRequestFullscreen();
        } else if (documentElement.mozRequestFullScreen) {
            documentElement.mozRequestFullScreen();
        } else if (documentElement.webkitRequestFullscreen) {
            documentElement.webkitRequestFullscreen();
        }
    } else {
        // 關閉全螢幕
        if (document.exitFullscreen) {
            document.exitFullscreen();
        } else if (document.mozCancelFullScreen) {
            document.mozCancelFullScreen();
        } else if (document.webkitCancelFullScreen) {
            document.webkitCancelFullScreen();
        } else if (document.msExitFullscreen) {
            document.msExitFullscreen();
        }
    }
    toggleStatus('fullscreen');
};

// 顯藏 logo
const toggleLogo = () => {
    eventBus.logoOpacity = Number(!operateModalButtonStatuses.logo);
    toggleStatus('logo');
};

// 剩餘時數計算
const timingStarted = ref(false);
const remainingTimes = ref([]);
const firstTimeId = ref(null);
const totalTimingSeconds = ref(0);
const timingSeconds = ref(0);
const timingStartDate = ref('');
const timingExpiryDate = ref('');
const getTimeBalancesResponse = async () => fetch(ziggyRoute(props, 'time-balances.index'))
    .then(response => {
        if (!response.ok) throw new Error(response.statusText)
        return response.json();
    })
    .catch(() => alert('Server Error'));
const getTimeBalances = response => (response?.data?.time_balances || []).slice(0, 2);
const updateTimeBalances = async () => fetch(ziggyRoute(props, 'time-balances.update-seconds'), {
    method: 'POST',
    headers: {
        'Content-Type': 'application/json',
    },
    body: JSON.stringify({
        id: firstTimeId.value,
        seconds: timingSeconds.value,
    }),
}).then(response => {
    if (!response.ok) throw new Error(response.statusText)
    return response.json();
}).catch(() => alert('Server Error'));

// 已完成ASR翻譯
const asrRecognized = ref(true);
const showTranscript = ref(false);
const refPhrase = ref(null);

const transcript = ref('');

const isLidExchange = ref(false);
const recognizer = new SpeechRecognizer();
recognizer.serviceMap(_.invert(props.recognizeServices))
    .serviceLangMap(props.recognizeServiceLanguages)
    .on('asr-recognizing', (original) => {
        asrRecognized.value = false;
        // 逐字稿
        transcript.value = original;
        // 開啟逐字稿
        showTranscript.value = true;
        scrollToBottom();
    })
    .on('asr-recognized', (original, translated) => {
        asrRecognized.value = true;
        if (translated !== undefined && translated !== '') {
            phrases.value.push({
                original,
                translated,
            });
            showTranscript.value = false;
            scrollToBottom();
        }
        if (shouldExchangeLang.value) {
            exchangeSourceTargetLanguages();
            shouldExchangeLang.value = false;
        }
        if (isLidExchange.value) {
            swapSourceTargetLanguages();
            stopRecognize();
            startRecognize();
            isLidExchange.value = false;
        }
    })
    .on('lid-recognized-lang-exchange', () => {
        isLidExchange.value = true;
    });
// 訊息產生後滑動到最下面
const scrollToBottom = async () => {
    await nextTick();

    refPhrase.value.scrollTo({
        top: refPhrase.value.scrollHeight,
        behavior: 'smooth'
    })
}

// 已連接azure/mimi準備翻譯
const recognizing = ref(false);
const timingIntervalId = ref(0);
const stopRecognize = (toUpdateTimeBalances = true) => {
    recognizing.value = false;
    recognizer.stop();
    // 停止計時器
    clearInterval(timingIntervalId.value);
    // 更新剩餘時間
    toUpdateTimeBalances && updateTimeBalances();
};
const startRecognize = async () => {
    languageSelects.source.openedOptionsFromUI = false;
    languageSelects.target.openedOptionsFromUI = false;

    recognizing.value = true;
    recognizer.stop();
    timingStarted.value = false;
    const timeBalancesResponse = await getTimeBalancesResponse();
    const remainingTimesData = getTimeBalances(timeBalancesResponse);
    if (!remainingTimesData.length) {
        resetNoticeModalSettings();
        noticeModalSettings.value.header = 'Attention';
        noticeModalSettings.value.bodyMessage = 'Hello, your service hours have now ended. Please proceed to add value to extend your service hours for usage.';
        noticeModalSettings.value.bodyMessageBold = false;
        noticeModalSettings.value.show = true;
    } else {
        const firstTimeRow = remainingTimesData[0];
        firstTimeId.value = firstTimeRow.id;
        totalTimingSeconds.value = timeFormatToSeconds(timeBalancesResponse?.data?.total_time || 0);
        timingSeconds.value = timeFormatToSeconds(firstTimeRow.have_time);
        timingStartDate.value = firstTimeRow.start_date;
        timingExpiryDate.value = firstTimeRow.expiry_date;
        timingStarted.value = true;
        // 啟用計時器
        timingIntervalId.value = setInterval(timing, 1000);
        remainingTimes.value = remainingTimesData;
    }
    if (!timingStarted.value) return;
    const traValue = getTraValueByAuto(
        recognizeButtonValues.tra,
        languageSelects.source.selected,
        languageSelects.target.selected
    );
    const asrNotSupport = !isRecognizeServiceLanguageSupport(
        recognizeButtonValues.asr,
        languageSelects.source.selected
    ), traNotSupport = !isTraSupport(
        traValue,
        languageSelects.source.selected,
        languageSelects.target.selected
    );
    if (asrNotSupport || traNotSupport) {
        openAsrOrTraNotSupportModal(
            recognizeButtonValues.asr,
            traValue,
            asrNotSupport,
            traNotSupport
        );
        return;
    }
    recognizeButtonValues.tra = traValue;
    recognizer
        .sourceLang(languageSelects.source.selected)
        .targetLang(languageSelects.target.selected)
        .asr(recognizeButtonValues.asr)
        .tra(recognizeButtonValues.tra)
        .when(recognizeButtonValues.lidAuto, recognizer => recognizer.openLid())
        .start();
};

// 開啟ASR或TRA不支援語言的提示彈窗
const openAsrOrTraNotSupportModal = (asrValue, traValue, asrNotSupport, traNotSupport) => {
    if (asrNotSupport && traNotSupport) {
        openNotSupportModal(`ASR${asrValue} and TRA${traValue} do not support this speech language and subtitle language`);
        return;
    }
    if (asrNotSupport) {
        openNotSupportModal(`ASR${asrValue} do not support this speech language and subtitle language`);
        return;
    }
    if (traNotSupport) {
        openNotSupportModal(`TRA${traValue} do not support this speech language and subtitle language`);
        return;
    }
};

// 時分秒格式轉秒
const timeFormatToSeconds = timeString => {
    const timeParts = timeString.split(':');
    const hours = parseInt(timeParts[0], 10);
    const minutes = parseInt(timeParts[1], 10);
    const seconds = parseInt(timeParts[2], 10);
    return (hours * 3600) + (minutes * 60) + seconds;
}

// 秒轉時分秒格式
const secondsToTimeFormat = seconds => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const remainingSeconds = seconds % 60;
    const formattedHours = String(hours).padStart(2, '0');
    const formattedMinutes = String(minutes).padStart(2, '0');
    const formattedSeconds = String(remainingSeconds).padStart(2, '0');
    return `${formattedHours}:${formattedMinutes}:${formattedSeconds}`;
}

// 固定打回後端的間隔秒數
const secondsCounter = ref(0);
// 執行倒數計時
const timing = () => {
    timingSeconds.value--;
    totalTimingSeconds.value--;
    secondsCounter.value++;
    // 秒數到了先停止後啟用
    if (timingSeconds.value <= 0) {
        // 停止計時器
        clearInterval(timingIntervalId.value);
        // 重新計時
        stopRecognize();
        startRecognize();
    } else {
        // 更新第一列時間
        const firstTimeRow = remainingTimes.value[0];
        firstTimeRow.have_time = secondsToTimeFormat(timingSeconds.value);
        firstTimeRow.start_date = timingStartDate.value;
        firstTimeRow.expiry_date = timingExpiryDate.value;
    }
    // 秒數計數
    if (secondsCounter.value === 5) {
        // 每跑5秒回打後端更新時間
        updateTimeBalances();
        secondsCounter.value = 0;
    }
};

// 重新倒數計時與開始翻譯
const restartTimingAndRecognize = () => {
    timingStarted.value && stopRecognize();
    startRecognize();
};

// 開關操作彈窗
const refOperateModal = ref(null);
watch(() => eventBus.onClickLogo, () => refOperateModal.value.classList.add('operate-modal--show'));
const closeOperateModal = () => {
    languageSelects.source.openedOptionsFromModal = false;
    languageSelects.target.openedOptionsFromModal = false;

    refOperateModal.value.classList.remove('operate-modal--show');
    // recognizing.value ? stopRecognize() : startRecognize();
}

// 監聽開關全螢幕的事件
const setFullScreenEventListeners = (add = true) => [
    'fullscreenchange',
    'webkitfullscreenchange',
    'mozfullscreenchange',
    'MSFullscreenChange',
].forEach(eventName => document[add ? 'addEventListener' : 'removeEventListener'](eventName, () => {
    // 當開關全螢幕是並非透過彈窗上的按鈕時(ex. 直接按下esc關閉)，全螢幕按鈕的狀態與文字須一併變更
    (!inFullscreen() && operateModalButtonStatuses.fullscreen)
        && (operateModalButtonStatuses.fullscreen = false);
    (inFullscreen() && !operateModalButtonStatuses.fullscreen)
        && (operateModalButtonStatuses.fullscreen = true);
}));

onMounted(async () => {
    setFullScreenEventListeners();
    const timeBalancesResponse = await getTimeBalancesResponse();
    remainingTimes.value = getTimeBalances(timeBalancesResponse);
});

onBeforeUnmount(() => setFullScreenEventListeners(false));
</script>

<template>
<div class="wrap d-flex flex-column flex-grow-1 vh-100">
    <div class="trigger" @click="onClickLogo"></div>
    <ul class="phrase flex-grow-1 overflow-auto d-flex flex-column"
        :class="[operateModalButtonStatuses.length ? 'w-50' : 'w-100', operateModalButtonStatuses.align ? 'ml-auto' : '']" ref="refPhrase"
    >
        <li class="phrase__item d-flex flex-column"
        :class="{
            'mt-auto': !index,
            'phrase__item--reverse': operateModalButtonStatuses.color,
            'align-self-start': !operateModalButtonStatuses.align,
            'align-self-end': operateModalButtonStatuses.align,
            'phrase__item--left-radius': !operateModalButtonStatuses.align,
            'phrase__item--right-radius': operateModalButtonStatuses.align,
        }"
            v-for="(phrase, index) in phrases" :key="index"
        >
            <p class="phrase__text text-break" :class="{
                'phrase__text--reverse': operateModalButtonStatuses.color,
                'phrase__text--scale' : operateModalButtonStatuses.reverseSpeech
            }" v-show="operateModalButtonStatuses.speech">{{ phrase.original }}</p>
            <p class="phrase__text text-break" :class="{
                'phrase__text--reverse': operateModalButtonStatuses.color,
                'phrase__text--scale' : operateModalButtonStatuses.reverseSubtitle
            }" v-show="operateModalButtonStatuses.subtitle">{{ phrase.translated }}</p>
        </li>
        <li v-if="showTranscript" class="phrase__item flex flex-column" :class="{
            'mt-auto': !phrases.length,
            'phrase__item--reverse': operateModalButtonStatuses.color,
            'align-self-start': !operateModalButtonStatuses.align,
            'align-self-end': operateModalButtonStatuses.align,
            'phrase__item--left-radius': !operateModalButtonStatuses.align,
            'phrase__item--right-radius': operateModalButtonStatuses.align,
        }">
            <p class="phrase__text text-break" :class="{
                'phrase__text--reverse': operateModalButtonStatuses.color,
                'phrase__text--scale' : operateModalButtonStatuses.reverseSpeech
            }">{{ transcript }}</p>
        </li>
    </ul>
    <div class="ui">
        <div class="toolbar">
            <!-- 清空翻譯視窗按鈕 -->
            <div class="d-flex flex-column justify-content-end">
                <button class="toolbar__button d-flex justify-content-center align-items-center"
                    v-if="showUIToolbar"
                    @click="resetTranslationWindow">
                    <svg class="flex-shrink-0 reset-icon" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 32 32">
                        <path fill-rule="evenodd" clip-rule="evenodd" d="M9.96662 5.76254C12.2458 4.44585 14.8946 3.91504 17.5051 4.25188C20.1156 4.58872 22.5429 5.77452 24.4132 7.62661C26.2835 9.47871 27.4929 11.8943 27.8552 14.5014C27.9464 15.1579 27.4882 15.764 26.8318 15.8552C26.1754 15.9464 25.5693 15.4882 25.4781 14.8318C25.1882 12.7461 24.2207 10.8136 22.7244 9.33193C21.2282 7.85026 19.2864 6.90162 17.198 6.63215C15.1096 6.36268 12.9905 6.78732 11.1672 7.84067C9.89272 8.57695 8.81217 9.59184 8.00058 10.7999H10.6666C11.3294 10.7999 11.8666 11.3372 11.8666 11.9999C11.8666 12.6627 11.3294 13.1999 10.6666 13.1999H6.02679C6.00862 13.2004 5.99041 13.2004 5.97217 13.1999H5.3333C4.67056 13.1999 4.1333 12.6627 4.1333 11.9999V6.66661C4.1333 6.00387 4.67056 5.46661 5.3333 5.46661C5.99604 5.46661 6.5333 6.00387 6.5333 6.66661V8.73975C7.47165 7.54541 8.63437 6.53219 9.96662 5.76254Z" fill="currentColor"/>
                        <path fill-rule="evenodd" clip-rule="evenodd" d="M22.0334 26.7907C19.7542 28.1074 17.1054 28.6382 14.4949 28.3013C11.8844 27.9645 9.45707 26.7787 7.5868 24.9266C5.71654 23.0745 4.5071 20.6589 4.14478 18.0518C4.05356 17.3954 4.51175 16.7893 5.16818 16.698C5.82462 16.6068 6.43072 17.065 6.52194 17.7214C6.81179 19.8071 7.77934 21.7396 9.27555 23.2213C10.7718 24.703 12.7136 25.6516 14.802 25.9211C16.8904 26.1905 19.0095 25.7659 20.8328 24.7126C22.1073 23.9763 23.1878 22.9614 23.9994 21.7533H21.3334C20.6706 21.7533 20.1334 21.216 20.1334 20.5533C20.1334 19.8905 20.6706 19.3533 21.3334 19.3533H25.9732C25.9914 19.3529 26.0096 19.3529 26.0278 19.3533H26.6667C27.3294 19.3533 27.8667 19.8905 27.8667 20.5533V25.8866C27.8667 26.5494 27.3294 27.0866 26.6667 27.0866C26.004 27.0866 25.4667 26.5494 25.4667 25.8866V23.8135C24.5284 25.0078 23.3656 26.021 22.0334 26.7907Z" fill="currentColor"/>
                    </svg>
                </button>
            </div>
            <!-- 來源語言選單 -->
            <div class="d-flex flex-column justify-content-end">
                <template v-if="showUIToolbar">
                    <span class="toolbar__label mb-2">Speech Language</span>
                    <button class="toolbar__button d-flex justify-content-center align-items-center"
                        @click="toggleLanguageOptions('source', 'ui')">
                        <div class="flex-grow-1 d-flex flex-column align-items-start justify-content-center">
                            <span class="text-nowrap leading-5">{{ languageSelects.source.selectedNativeText.replace('(Beta)', '') }}</span>
                            <span class="leading-5" v-if="languageSelects.source.selectedNativeText.includes('(Beta)')">(Beta)</span>
                        </div>
                        <svg :class="{ 'rotate-180': languageSelects.source.openedOptionsFromUI }" class="select-icon flex-shrink-0" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">
                            <path fill-rule="evenodd" clip-rule="evenodd" d="M5.3637 8.36358C5.71517 8.01211 6.28502 8.01211 6.63649 8.36358L12.0001 13.7272L17.3637 8.36358C17.7152 8.01211 18.285 8.01211 18.6365 8.36358C18.988 8.71505 18.988 9.2849 18.6365 9.63637L12.6365 15.6364C12.285 15.9878 11.7152 15.9878 11.3637 15.6364L5.3637 9.63637C5.01223 9.2849 5.01223 8.71505 5.3637 8.36358Z" fill="currentColor"/>
                        </svg>
                        <ul class="toolbar__options" :class="{
                            'toolbar__options--active': languageSelects.source.openedOptionsFromUI
                         }">
                            <li v-for="(text, value) in languageNativeOptions" :key="value" @click.stop="selectLanguage('source', value, 'ui')">
                                <span class="leading-5 text-nowrap">{{ text.replace('(Beta)', '') }}</span>
                                <span class="leading-5" v-if="text.includes('(Beta)')">(Beta)</span>
                            </li>
                        </ul>
                    </button>
                </template>
            </div>
            <!-- 交換來源與目標語言按鈕 -->
            <div class="d-flex flex-column justify-content-end">
                <button class="toolbar__button d-flex justify-content-center align-items-center"
                    v-if="showUIToolbar"
                    @click="exchangeSourceTargetLanguages">
                    <svg class="flex-shrink-0 switch-icon" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 32 32">
                        <path fill-rule="evenodd" clip-rule="evenodd" d="M18.3515 28.0486C18.8201 28.5172 19.5799 28.5172 20.0485 28.0486L24.8485 23.2486C25.3172 22.7799 25.3172 22.0201 24.8485 21.5515L20.0485 16.7515C19.5799 16.2829 18.8201 16.2829 18.3515 16.7515C17.8828 17.2201 17.8828 17.9799 18.3515 18.4486L21.1029 21.2L11.2 21.2C10.5373 21.2 10 21.7373 10 22.4C10 23.0628 10.5373 23.6 11.2 23.6L21.1029 23.6L18.3515 26.3515C17.8828 26.8201 17.8828 27.5799 18.3515 28.0486Z" fill="currentColor"/>
                        <path fill-rule="evenodd" clip-rule="evenodd" d="M13.6485 3.95145C13.1799 3.48282 12.4201 3.48282 11.9515 3.95145L7.15147 8.75145C6.68284 9.22008 6.68284 9.97987 7.15147 10.4485L11.9515 15.2485C12.4201 15.7171 13.1799 15.7171 13.6485 15.2485C14.1172 14.7799 14.1172 14.0201 13.6485 13.5514L10.8971 10.8L20.8 10.8C21.4627 10.8 22 10.2627 22 9.59998C22 8.93723 21.4627 8.39998 20.8 8.39998L10.8971 8.39998L13.6485 5.6485C14.1172 5.17987 14.1172 4.42008 13.6485 3.95145Z" fill="currentColor"/>
                    </svg>
                </button>
            </div>
            <!-- 目標來源選單 -->
            <div class="d-flex flex-column justify-content-end">
                <template v-if="showUIToolbar">
                    <div class="toolbar__label mb-2">Subtitle Language</div>
                    <button class="toolbar__button d-flex justify-content-center align-items-center"
                        @click="toggleLanguageOptions('target', 'ui')">
                        <div class="flex-grow-1 d-flex flex-column align-items-start justify-content-center">
                            <span class="text-nowrap leading-5">{{ languageSelects.target.selectedNativeText.replace('(Beta)', '') }}</span>
                            <span class="leading-5" v-if="languageSelects.target.selectedNativeText.includes('(Beta)')">(Beta)</span>
                        </div>
                        <svg :class="{ 'rotate-180': languageSelects.target.openedOptionsFromUI }" class="select-icon flex-shrink-0" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">
                            <path fill-rule="evenodd" clip-rule="evenodd" d="M5.3637 8.36358C5.71517 8.01211 6.28502 8.01211 6.63649 8.36358L12.0001 13.7272L17.3637 8.36358C17.7152 8.01211 18.285 8.01211 18.6365 8.36358C18.988 8.71505 18.988 9.2849 18.6365 9.63637L12.6365 15.6364C12.285 15.9878 11.7152 15.9878 11.3637 15.6364L5.3637 9.63637C5.01223 9.2849 5.01223 8.71505 5.3637 8.36358Z" fill="currentColor"/>
                        </svg>
                        <ul class="toolbar__options" :class="{
                            'toolbar__options--active': languageSelects.target.openedOptionsFromUI
                        }">
                            <li v-for="(text, value) in languageNativeOptions" :key="value" @click.stop="selectLanguage('target', value, 'ui')">
                                <span class="text-nowrap leading-5">{{ text.replace('(Beta)', '') }}</span>
                                <span class="leading-5" v-if="text.includes('(Beta)')">(Beta)</span>
                            </li>
                        </ul>
                    </button>
                </template>
           </div>
           <!-- 開始/結束翻譯按鈕 -->
           <div class="d-flex flex-column justify-content-end">
                <button class="toolbar__button d-flex justify-content-center align-items-center"
                    v-if="showUIToolbar && !recognizing"
                    @click="startRecognize"
                >
                    <span>START</span>
                </button>
                <button class="toolbar__button toolbar__button--active d-flex justify-content-center align-items-center"
                    v-if="showUIToolbar && recognizing"
                    @click="stopRecognize">
                    <span>STOP</span>
                </button>
           </div>
           <!-- 顯藏主畫面操作列按鈕 -->
           <div class="d-flex flex-column justify-content-end">
                <button class="toolbar__button d-flex justify-content-center align-items-center" @click="toggleUIToolbar">
                    <svg :class="{ 'rotate-180': !showUIToolbar }" class="flex-shrink-0 toggle-icon" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 32 32">
                        <path fill-rule="evenodd" clip-rule="evenodd" d="M7.15128 11.1515C7.61991 10.6829 8.3797 10.6829 8.84833 11.1515L15.9998 18.303L23.1513 11.1515C23.6199 10.6829 24.3797 10.6829 24.8483 11.1515C25.317 11.6201 25.317 12.3799 24.8483 12.8486L16.8483 20.8486C16.3797 21.3172 15.6199 21.3172 15.1513 20.8486L7.15128 12.8486C6.68265 12.3799 6.68265 11.6201 7.15128 11.1515Z" fill="currentColor"/>
                    </svg>
                </button>
           </div>
        </div>
        <div class="transformer" v-if="showUIToolbar" :class="{
            'd-none': !operateModalButtonStatuses.asr && !operateModalButtonStatuses.tra && !operateModalButtonStatuses.lid
        }">
            <template v-if="operateModalButtonStatuses.asr">
                <div class="d-flex flex-column justify-content-end">
                    <span class="transformer__label mb-2">ASR</span>
                    <button class="transformer__button d-flex justify-content-center align-items-center" :class="[
                        { 'transformer__button--active' : recognizeButtonValues.asr === 1 },
                    ]" @click="prepareToSetAsr(1)">
                        <span>1</span>
                    </button>
                </div>
                <div class="d-flex flex-column justify-content-end">
                    <button class="transformer__button d-flex justify-content-center align-items-center" :class="[
                        { 'transformer__button--active': recognizeButtonValues.asr === 2 },
                    ]" @click="prepareToSetAsr(2)">
                        <span>2</span>
                    </button>
                </div>
            </template>
            <template v-if="operateModalButtonStatuses.tra">
                <div class="d-flex flex-column justify-content-end">
                    <span class="transformer__label mb-2">TRA</span>
                    <button class="transformer__button d-flex justify-content-center align-items-center" :class="[
                        { 'transformer__button--active': recognizeButtonValues.tra === 1 },
                    ]" @click="prepareToSetTra(1)">
                        <span>1</span>
                    </button>
                </div>
                <div class="d-flex flex-column justify-content-end">
                    <button class="transformer__button d-flex justify-content-center align-items-center" :class="[
                        { 'transformer__button--active': recognizeButtonValues.tra === 2 },
                    ]" @click="prepareToSetTra(2)">
                        <span>2</span>
                    </button>
                </div>
                <div class="d-flex flex-column justify-content-end">
                    <button class="transformer__button d-flex justify-content-center align-items-center" :class="[
                        { 'transformer__button--active': recognizeButtonValues.traAuto },
                    ]" @click="toggleTraAuto">
                        <span>Auto</span>
                    </button>
                </div>
            </template>
            <template v-if="operateModalButtonStatuses.lid">
                <div class="d-flex flex-column justify-content-end">
                    <span class="transformer__label mb-2">LID</span>
                    <button class="transformer__button d-flex justify-content-center align-items-center" :class="[
                        { 'transformer__button--active': recognizeButtonValues.lidAuto },
                    ]" @click="toggleLidAuto">
                        <span>Auto</span>
                    </button>
                </div>
            </template>
        </div>
        <div v-show="showUIToolbar" class="time" :class="{
            'd-none': !operateModalButtonStatuses.timer
        }">
            <span v-for="time in remainingTimes" class="time__text">Remaining Time：{{ time.have_time }} Date of Service : {{ time.start_date }} ~ {{ time.expiry_date }}</span>
        </div>
    </div>
</div>

<!-- 操作彈窗 -->
<div class="operate-modal-wrap" id="operate-modal-wrap">
    <div id="operate-modal" class="operate-modal " ref="refOperateModal">
    <!-- 登出按鈕 -->
        <div class="operate-modal__header d-flex align-items-center">
            <span>ID: {{ page.props.authUser?.name }}</span>
            <form class="ml-auto" @submit.prevent="submitLogoutForm">
                <button type="submit" id="logout__btn" class="logout">Log out</button>
            </form>
        </div>
        <div class="operate-modal__selects">
            <!-- 彈窗中的來源語言選單 -->
            <div class="d-flex flex-column justify-content-end">
                <span class="operate-modal__label mb-2">Speech Language</span>
                <button class="operate-modal__select d-flex justify-content-center align-items-center"
                    @click="toggleLanguageOptions('source', 'modal')">
                    <div class="flex-grow-1 d-flex flex-column align-items-start justify-content-center">
                        <span class="text-nowrap leading-5">{{ languageSelects.source.selectedText.replace('(Beta)', '') }}</span>
                        <span class="leading-5" v-if="languageSelects.source.selectedText.includes('(Beta)')">(Beta)</span>
                    </div>
                    <svg class="flex-shrink-0" xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                        <path fill-rule="evenodd" clip-rule="evenodd" d="M5.3637 8.3637C5.71517 8.01223 6.28502 8.01223 6.63649 8.3637L12.0001 13.7273L17.3637 8.3637C17.7152 8.01223 18.285 8.01223 18.6365 8.3637C18.988 8.71517 18.988 9.28502 18.6365 9.63649L12.6365 15.6365C12.285 15.988 11.7152 15.988 11.3637 15.6365L5.3637 9.63649C5.01223 9.28502 5.01223 8.71517 5.3637 8.3637Z" fill="black"/>
                    </svg>
                    <ul class="operate-modal__options" :class="[
                        { 'operate-modal__options--active': languageSelects.source.openedOptionsFromModal },
                    ]">
                        <li v-for="(text, value) in languageOptions" @click.stop="selectLanguage('source', value, 'modal')" :key="value">
                            <span class="text-nowrap leading-5">{{ text.replace('(Beta)', '') }}</span>
                            <span class="leading-5" v-if="text.includes('(Beta)')">(Beta)</span>
                        </li>
                    </ul>
                </button>
            </div>
            <!-- 彈窗中的目標語言選單 -->
            <div class="d-flex flex-column justify-content-end">
                <span class="operate-modal__label mb-2">Subtitle Language</span>
                <button class="operate-modal__select d-flex justify-content-center align-items-center"
                    @click="toggleLanguageOptions('target', 'modal')">
                    <div class="flex-grow-1 d-flex flex-column align-items-start justify-content-center">
                        <span class="text-nowrap leading-5">{{ languageSelects.target.selectedText.replace('(Beta)', '') }}</span>
                        <span class="leading-5" v-if="languageSelects.target.selectedText.includes('(Beta)')">(Beta)</span>
                    </div>
                    <svg class="flex-shrink-0" xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                        <path fill-rule="evenodd" clip-rule="evenodd" d="M5.3637 8.3637C5.71517 8.01223 6.28502 8.01223 6.63649 8.3637L12.0001 13.7273L17.3637 8.3637C17.7152 8.01223 18.285 8.01223 18.6365 8.3637C18.988 8.71517 18.988 9.28502 18.6365 9.63649L12.6365 15.6365C12.285 15.988 11.7152 15.988 11.3637 15.6365L5.3637 9.63649C5.01223 9.28502 5.01223 8.71517 5.3637 8.3637Z" fill="black"/>
                    </svg>
                    <ul class="operate-modal__options" :class="[
                        { 'operate-modal__options--active': languageSelects.target.openedOptionsFromModal },
                    ]">
                        <li v-for="(text, value) in languageOptions" @click.stop="selectLanguage('target', value, 'modal')" :key="value">
                            <span class="text-nowrap leading-5">{{ text.replace('(Beta)', '') }}</span>
                            <span class="leading-5" v-if="text.includes('(Beta)')">(Beta)</span>
                        </li>
                    </ul>
                </button>
            </div>
        </div>
        <div class="operate-modal__body">
            <button @click="toggleStatus('timer')">TIMER {{ operateModalButtonTexts.timer }}</button>
            <button @click="toggleStatus('align')">START {{ operateModalButtonTexts.align }}</button>
            <button @click="toggleStatus('reverseSpeech')">Reverse Speech {{ operateModalButtonTexts.reverseSpeech }}</button>
            <button @click="toggleStatus('reverseSubtitle')">Reverse Subtitle {{ operateModalButtonTexts.reverseSubtitle }}</button>
            <button @click="toggleStatus('speech')">Speech {{ operateModalButtonTexts.speech }}</button>
            <button @click="toggleStatus('subtitle')">Subtitle {{ operateModalButtonTexts.subtitle }}</button>
            <button @click="toggleStatus('asr')">ASR {{ operateModalButtonTexts.asr }}</button>
            <button @click="toggleStatus('tra')">TRA {{ operateModalButtonTexts.tra }}</button>
            <button @click="toggleStatus('lid')">LID UI {{ operateModalButtonTexts.lid }}</button>
            <button class="non-keep" @click="toggleFullscreen">FULL SCREEN {{ operateModalButtonTexts.fullscreen }}</button>
            <button @click="toggleLogo">LOGO {{ operateModalButtonTexts.logo }}</button>
            <button @click="toggleStatus('color')">COLOR {{ operateModalButtonTexts.color }}</button>
            <button class="non-keep" @click="toggleStatus('length')">LENGTH {{ operateModalButtonTexts.length }}</button>
        </div>
        <div class="operate-modal__footer d-flex flex-column align-items-center">
            <!-- 開始/結束翻譯按鈕 -->
            <button class="start"  @click="closeOperateModal">OK</button>
            <span>Version：{{ page.props.appVersion }}</span>
        </div>
    </div>
</div>

<!-- 通用的提示彈窗 -->
<!-- `display: block !important;`: 蓋掉 class modal 的 display: none; 用-->
<div class="modal" style="display: block !important;" tabindex="-1" aria-hidden="true" v-show="noticeModalSettings.show">
    <div class="modal-dialog modal-dialog-centered" style="width: 25rem">
        <div class="modal-content rounded-0">
            <div class="modal-header" v-if="noticeModalSettings.header !== ''">
                <h5 class="modal-title">{{ noticeModalSettings.header }}</h5>
                <button type="button" class="close" aria-label="Close" @click="closeNoticeModal">
                    <span aria-hidden="true">&times;</span>
                </button>
            </div>
            <div class="modal-body">
                <div class="row flex-column justify-content-center" style="height: 8rem">
                    <h5 class="text-center font-weight-bold" v-if="noticeModalSettings.bodyMessageBold">
                        {{ noticeModalSettings.bodyMessage }}
                    </h5>
                    <template v-else>{{ noticeModalSettings.bodyMessage }}</template>
                </div>
            </div>
            <div class="row justify-content-center mb-4">
                <div class="col-4" v-if="noticeModalSettings.confirmCallback !== null">
                    <button type="button" class="btn black-btn" style="width: 100%" @click="confirmNoticeModal">
                        Confirm
                    </button>
                </div>
                <div class="col-4">
                    <button type="button" class="btn black-btn" style="width: 100%" @click="closeNoticeModal">
                        {{ noticeModalSettings.closeButtonText }}
                    </button>
                </div>
            </div>
        </div>
    </div>
</div>
</template>
